<script setup lang="ts">
import { RotateCcw } from '@lucide/vue'
import { planAllowanceNotice } from '@zerohuman/shared'
import {
    Button,
    type DecisionSection,
    type DecisionView,
    MOTION,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    type StatusTone,
    ZhDecisionActions,
    ZhDecisionCard,
    ZhDecisionLead,
    ZhDecisionSections,
    ZhPageTitle,
    ZhSectionHeading,
} from '@zerohuman/ui'
import { computed, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import {
    api,
    type CeoQuestionAnswer,
    ceoQuestions,
    formatGatePayload,
    formatWaited,
    type GateAsker,
    type GateDecision,
    type GateRow,
    gateAged,
    gateChoiceLabel,
    gateOverHour,
    gateTaskLabel,
    type ToolNotBoundNeedsYouItem,
    waitedMs,
} from '@/api'
import DocsLink from '@/components/DocsLink.vue'
import FirstRunHint from '@/components/FirstRunHint.vue'
import GateCardLink from '@/components/GateCardLink.vue'
import GateChoiceForm from '@/components/GateChoiceForm.vue'
import GateDecisionActions from '@/components/GateDecisionActions.vue'
import ListSearchField from '@/components/ListSearchField.vue'
import ProposedWorkCard from '@/components/ProposedWorkCard.vue'
import { useClientTable } from '@/composables/useClientTable'
import { useGateDecisions } from '@/composables/useGateDecisions'
import { useListState } from '@/composables/useListState'
import { useLiveResource } from '@/composables/useLiveResource'
import { useUiMode } from '@/composables/useUiMode'
import { requireAiAccount } from '@/lib/aiAccountGuard'
import { type GateKind, gateKind } from '@/lib/gateKind'
import { type ActionLead, actionLead, choiceLead } from '@/lib/gateLead'
import { gateDecisionSections } from '@/lib/gateSummary'
import { grantAllowsScreen, type NavGrant } from '@/lib/navGrant'
import { toolLabel } from '@/lib/presentation'

/**
 * A waiting gate as its decision card (ZhDecisionCard) draws it, as zerohuman.com's Control figures draw theirs: the
 * team and the member asking, what approving does (the run's own title, else the task), why it waits for a person,
 * the decision context under its own headings, and how long it has waited. `payloadPreview` is never shown — the
 * payload belongs on the run page — but it stays on the row so a search still finds a gate by the question it asks.
 */
type GateCard = GateRow & {
    taskLabel: string
    /** The tool as this mode names it (#1749): the vendor's name in Standard, the raw tool name in Advanced. */
    tool: string
    alert: 'aged' | 'overHour' | ''
    /** The decision you just made on it, shown on the card until it leaves. */
    decided?: GateDecision
    /** The choice that decision was made through, in its button's words, when the gate offers choices. */
    decidedLabel?: string
    payloadPreview: string
    /** Searchable decision context: the stored summary, or an email escalation's carried mail fields. */
    summaryText: string
    execution: string
    decision: DecisionView
    /** What deciding it does (#1760): an action to approve, or a question to answer. */
    kind: GateKind
    /**
     * A card's one line: an action's summary line and its recommendation, or a gate with choices' summary and what it
     * suggests. Null on a CEO question (its form leads), or with no summary to lead with: a lead is never made up.
     */
    lead: ActionLead | null
    /** An action's summary under its headings, folded under "Show details" below its lead. Empty on a question. */
    folded: DecisionSection[]
    /** What a gate with its own choices decides on, in view under its lead: never folded. Empty on any other gate. */
    shown: DecisionSection[]
}

/** The orders the queue can be read in. Oldest first by default: the gate that has waited longest. */
const SORTS = [
    { key: 'waited', label: 'Waited longest', sort: 'createdAt', order: 'ASC' },
    { key: 'newest', label: 'Newest', sort: 'createdAt', order: 'DESC' },
    { key: 'task', label: 'Task', sort: 'taskLabel', order: 'ASC' },
    { key: 'execution', label: 'Execution', sort: 'execution', order: 'ASC' },
    { key: 'tool', label: 'Tool', sort: 'tool', order: 'ASC' },
] as const

/** What the card's state says, and in which tone: past 24h is the failed tone; anything younger waits on you. */
const ALERT: Record<GateCard['alert'], { status: string; tone: StatusTone }> = {
    aged: { status: 'Aged: past 24 hours', tone: 'failed' },
    overHour: { status: 'Waiting over an hour', tone: 'action' },
    '': { status: 'Waiting for your decision', tone: 'action' },
}

/**
 * The queue is read every five seconds, never two reads at once, and not while the tab is hidden: the
 * waiting-gates read is costly to build (each gate's task and execution), and a gate waits for minutes,
 * not seconds. `now` still ticks every second, so the wait time and the aged state stay live.
 */
const QUEUE_READ_MS = 5000
const {
    data,
    error: loadError,
    loading,
    now,
    refresh,
} = useLiveResource(
    () => 'gates',
    () => api.waitingGates(),
    { everyMs: QUEUE_READ_MS },
)
const error = ref<string | null>(null)
/** A decided card leaves the queue once it has faded; the cards after it then slide into its place. */
const decisions = useGateDecisions(MOTION.decisionMs)
const gates = computed(() => (data.value ?? []).filter(decisions.onPage))

/** #1749: Standard prints no raw tool name on a card or in the Tool filter; Advanced shows it as it always did. */
const { mode: uiMode } = useUiMode()
const advancedMode = computed(() => uiMode.value === 'advanced')

/**
 * #2122: a reached plan limit is one Needs you item, however many runs it paused, read beside the queue on the same
 * rhythm and gone at the first read after the limit clears. A read that fails keeps what the last one said.
 */
const { data: planLimitData } = useLiveResource(
    () => 'gates:plan-limits',
    () => api.planLimitItems(),
    { everyMs: QUEUE_READ_MS },
)

/**
 * #637: a member whose runs are parked because no binding of theirs reaches a tool is one Needs you item per member
 * and vendor, naming both, with a link to that member's Tools for the vendor. Read beside the queue on the same
 * rhythm; gone at the first read after none of their runs is parked on the vendor. A failed read keeps the last one.
 */
const { data: toolNotBoundData } = useLiveResource(
    () => 'gates:tool-not-bound',
    () => api.toolNotBoundItems(),
    { everyMs: QUEUE_READ_MS },
)
const toolNotBoundCards = computed(() => toolNotBoundData.value ?? [])

/**
 * KPIs nobody has signed off yet: one item per owner (the company, a team, a role or a task), naming them, saying that
 * no run sees them until a person signs them off and which scheduled tasks record nothing meanwhile, with a link to
 * where they are signed off. They count in the sidebar's Gates badge. Read beside the queue on the same rhythm; gone at
 * the first read after the last one is signed off or withdrawn. A failed read keeps the last one.
 */
const { data: kpiSignOffData } = useLiveResource(
    () => 'gates:kpi-sign-offs',
    () => api.kpiSignOffItems(),
    { everyMs: QUEUE_READ_MS },
)
const kpiSignOffCards = computed(() => kpiSignOffData.value ?? [])

/**
 * #849: what one run proposed for a team's work list is one decision here, beside the gates: each item with Approve,
 * Hold and Drop, and Approve all. Read on the same rhythm, and read again as soon as one is answered; a batch whose
 * last item is decided anywhere (here, in chat, on the board) is gone at the next read.
 */
const { data: proposedData, refresh: refreshProposed } = useLiveResource(
    () => 'gates:proposed-work',
    () => api.proposedWork(),
    { everyMs: QUEUE_READ_MS },
)
const proposedBatches = computed(() => proposedData.value ?? [])

/**
 * The notice's text: the API's plain sentence (the vendor's display name, each tool in words). Advanced adds the exact
 * tool names, as every other screen does for a machine value (#1749); Standard never shows them.
 */
function toolNotBoundWhy(item: ToolNotBoundNeedsYouItem): string {
    return uiMode.value === 'advanced' && item.tools.length > 0
        ? `${item.sentence} (${item.tools.join(', ')})`
        : item.sentence
}

/**
 * The viewer's grant, read once there is a plan-limit item to offer Upgrade on: `undefined` until then, null when it
 * could not be read. The button shows only where the grant opens Billing, the rule the sidebar and router apply.
 */
const viewerGrant = ref<NavGrant | null | undefined>(undefined)

watch(
    () => planLimitData.value?.length ?? 0,
    async count => {
        if (count === 0 || viewerGrant.value !== undefined) return
        try {
            viewerGrant.value = await api.me()
        } catch {
            viewerGrant.value = null
        }
    },
)

/**
 * Each item in this mode's words (Advanced adds the exact counters), and whether its Upgrade button shows. A reader
 * whose access does not include Billing gets no button, so their words say who to ask to upgrade instead.
 */
const planLimitCards = computed(() =>
    (planLimitData.value ?? []).map(item => {
        const showsButton = viewerGrant.value !== undefined && grantAllowsScreen(viewerGrant.value, item.buttonPath)
        // Until the grant is read, nothing is known against the reader: the words stay those of the API's item.
        const canUpgrade = viewerGrant.value === undefined || showsButton
        return {
            unit: item.unit,
            sentence: planAllowanceNotice(item.allowance, uiMode.value, canUpgrade)?.sentence ?? item.sentence,
            buttonLabel: item.buttonLabel,
            buttonPath: item.buttonPath,
            showsButton,
        }
    }),
)

/**
 * A card's one line in Needs you: an action's from its summary, a gate with choices' from what it decides on. A CEO
 * question has none here, as its form leads; and nothing to lead with is no lead, never a made-up one.
 */
function cardLead(kind: GateKind, choosing: boolean, sections: DecisionSection[]): ActionLead | null {
    if (kind === 'action') {
        return actionLead(sections)
    }
    return choosing ? choiceLead(sections) : null
}

/** Who is asking, as the card's head says it after the team: "Ava, Content editor". Null when neither is known. */
function askerLine(asker: GateAsker | null | undefined): string | null {
    return [asker?.name, asker?.role].filter(Boolean).join(', ') || null
}

/** What a card shows that only changes when the gates do, worked out once per read rather than every tick. */
const read = computed(() =>
    gates.value.map(gate => {
        const sections = gateDecisionSections(gate)
        const kind = gateKind(gate)
        /** A question with no questions of its own to answer: a gate with its own choices. */
        const choosing = kind === 'question' && ceoQuestions(gate) === null
        return {
            gate,
            taskLabel: gateTaskLabel(gate),
            tool: toolLabel(gate.toolName, uiMode.value),
            payloadPreview: formatGatePayload(gate.payload).preview,
            summaryText: sections.flatMap(section => [section.heading ?? '', ...section.lines]).join(' '),
            execution: gate.executionName ?? '',
            kind,
            // #1760: an action leads with one line and folds its summary; a CEO question's form leads and folds its
            // own; a gate with its own choices leads with its summary and what it suggests, and keeps what it decides
            // on in view under that line, as its choices are made on it.
            lead: cardLead(kind, choosing, sections),
            folded: kind === 'action' ? sections : [],
            shown: choosing ? sections : [],
        }
    }),
)

const cards = computed<GateCard[]>(() =>
    read.value.map(({ gate, ...labels }) => ({
        ...gate,
        ...labels,
        alert: alertState(gate),
        decided: decisions.decidedOf(gate.id),
        decidedLabel: decisions.decidedLabelOf(gate.id),
        decision: {
            title: gate.title ?? labels.taskLabel,
            why: gate.why ?? undefined,
            from: gate.askedBy?.team ?? gate.executionName ?? 'No execution',
            by: askerLine(gate.askedBy) ?? undefined,
            waited: formatWaited(waitedMs(gate, now.value)),
        },
    })),
)

const table = useClientTable({
    data: cards,
    searchFields: ['taskLabel', 'execution', 'toolName', 'tool', 'title', 'why', 'payloadPreview', 'summaryText'],
    defaultSort: 'createdAt',
    defaultOrder: 'ASC',
})

const sortKey = computed<string>({
    get: () =>
        SORTS.find(option => option.sort === table.sort.value && option.order === table.order.value)?.key ?? 'waited',
    set: key => {
        const option = SORTS.find(candidate => candidate.key === key) ?? SORTS[0]
        table.sort.value = option.sort
        table.order.value = option.order
    },
})

/** The kit Select takes a choice as a value; the sort is keyed by the `SORTS` key it says. */
function chooseSort(key: unknown): void {
    sortKey.value = String(key)
}

/** The executions and tools waiting right now, for the filters: never an option that matches nothing. */
const executions = computed(() => [...new Set(cards.value.map(card => card.execution).filter(Boolean))].sort())
const tools = computed(() => [...new Set(cards.value.map(card => card.tool))].sort())

/**
 * What a filter's "All" stands for in its kit Select, which has no empty value to give an item. It never reaches
 * the table: choosing it clears the filter to the empty string, exactly as the empty option always did.
 */
const ALL = ':all'

/** The fields the toolbar filters on. The tool filter reads the card's `tool`, which is the raw name in Advanced. */
type FilterKey = 'execution' | 'tool'

function filterOf(key: FilterKey): string {
    return table.filters.value[key] ?? ''
}

function setFilter(key: FilterKey, value: string): void {
    table.filters.value = { ...table.filters.value, [key]: value }
}

/** What a filter's Select holds: its choice, or "All" while it filters nothing. */
function filterChoice(key: FilterKey): string {
    return filterOf(key) || ALL
}

function chooseFilter(key: FilterKey, value: unknown): void {
    setFilter(key, value === ALL ? '' : String(value))
}

/**
 * The Tool filter holds the label of the mode it was chosen in (a vendor in Standard, a raw name in Advanced), and the
 * sidebar switch can flip the mode with this page open. A switch clears it, so the queue never narrows on a label no
 * card has any more and its Select never holds a value it does not offer.
 */
watch(advancedMode, () => {
    if (filterOf('tool')) {
        setFilter('tool', '')
    }
})

/**
 * #633: the search, sort and execution / tool filters come back on the next visit. A saved execution or tool no
 * longer waiting is dropped once the queue is read; until then it is kept.
 */
const list = useListState('gates', {
    ...table.listFields,
    filters: {
        ref: table.filters,
        allowed: () => (data.value ? [...executions.value, ...tools.value] : null),
    },
})

/** Narrowed by a search, a filter or a sort other than waited longest: Clear filters puts the queue back. */
const narrowed = computed(() => !list.isDefault.value)

function clearFilters(): void {
    list.clear()
}

// A narrower queue starts from its first page.
watch([table.search, table.filters], () => {
    table.page.value = 1
})

const shown = computed(() => {
    const total = table.total.value
    if (total === 0) {
        return 'Showing 0 of 0'
    }
    const from = (table.page.value - 1) * table.limit.value + 1
    return `Showing ${from}–${from + table.paginated.value.length - 1} of ${total}`
})

/**
 * No gate is waiting: the queue (with its search and filters) is not worth showing, so the page teaches the
 * loop instead. Keyed off the gates the API returned, never the filtered cards — a search that narrows real
 * gates to zero must keep the search, or there is no way to clear it.
 */
const noGates = computed(() => !loading.value && gates.value.length === 0)

/**
 * A decision shows on the card and takes it off the queue once it has faded, without waiting on the API; if
 * the API refuses it, the card comes back as it was. The queue is read again only once the card has gone.
 * `record` sends it: a bare decision by default, or a CEO question's answers and note from its form, or the
 * choice made on a gate that offers its own, which the card then names (`label`) in place of Approved.
 */
async function decide(
    id: string,
    decision: GateDecision,
    record = () => api.decideGate(id, decision),
    label?: string,
): Promise<void> {
    error.value = null
    // #1796: a decision resumes the run it holds, which is model work, so without an AI account nothing is sent and
    // the prompt opens.
    if (!(await requireAiAccount())) return
    if (await decisions.decide(id, decision, record, label)) {
        void refresh()
    } else {
        error.value = 'Could not record decision (already decided?).'
    }
}

/**
 * A CEO question's decision from its form: the answers and note, sent on whole with the payload as the CEO edited it
 * when they did (os#911), exactly as the run page does.
 */
function decideQuestions(
    id: string,
    decision: GateDecision,
    note: string,
    answers?: CeoQuestionAnswer[],
    edit?: Record<string, unknown>,
): Promise<void> {
    return decide(id, decision, () =>
        edit ? api.decideGate(id, decision, note, answers, edit) : api.decideGate(id, decision, note, answers),
    )
}

/** The choice made on a gate that offers its own, which the card then names in place of Approved. */
function decideChoice(card: GateRow, id: string, decision: GateDecision, note: string, choice: string): Promise<void> {
    return decide(
        id,
        decision,
        () => api.decideGate(id, decision, note, undefined, undefined, choice),
        gateChoiceLabel(card, choice),
    )
}

/** Aged (past `ageAt`, ~24h) shouts; waiting over an hour says so; fresh gates stay quiet. */
function alertState(gate: GateRow): GateCard['alert'] {
    if (gateAged(gate, now.value)) {
        return 'aged'
    }
    if (gateOverHour(gate, now.value)) {
        return 'overHour'
    }
    return ''
}
</script>

<template>
	<section class="w-full space-y-4 p-8">
		<ZhPageTitle>
			Gates
			<template #meta><DocsLink path="/docs/control/gates" /></template>
		</ZhPageTitle>
		<!-- #1762 (L5): the website's line for gates leads the page in plain words, as Tasks, Chat, Tools and Spend open
			with theirs; the detail below it reads as it always has. -->
		<p class="text-muted-foreground text-sm">What you approve is exactly what runs.</p>
		<p class="text-muted-foreground text-sm">
			Waiting CEO approvals: the task it is for, the tool it wants, what the run says it does, and how long it
			has waited. A gate past an hour says so; one past 24h is marked aged. Approve runs the stored payload.
			Reject cancels. An approval is never approved by silence and never cancelled for its age: it waits until
			someone decides it. The enterprise's owners are emailed a reminder after about 24 hours and again after about
			48 hours, and none after that. A sign-in request is not an approval, so it still closes at the enterprise's
			gate timeout.
		</p>
		<p v-if="error" class="text-destructive text-sm">{{ error }}</p>
		<p v-else-if="loadError" class="text-destructive text-sm">Could not load gates.</p>
		<!--
			#2122: one item per reached plan limit, however many runs it paused: the shared reached sentence and Upgrade
			to Billing. Nothing on it is decided here; it leaves once the limit clears.
		-->
		<ol
			v-if="planLimitCards.length"
			class="m-0 grid list-none gap-4 p-0 lg:grid-cols-2 2xl:grid-cols-3"
			aria-label="Plan limits reached"
			data-testid="plan-limit-items"
		>
			<li v-for="item in planLimitCards" :key="item.unit" class="grid" :data-plan-limit-unit="item.unit">
				<ZhDecisionCard
					:decision="{ title: 'Plan limit reached', from: 'Your plan', why: item.sentence }"
					status="Paused by your plan"
					status-tone="failed"
				>
					<template v-if="item.showsButton" #actions>
						<Button as-child size="sm">
							<RouterLink :to="item.buttonPath">{{ item.buttonLabel }}</RouterLink>
						</Button>
					</template>
				</ZhDecisionCard>
			</li>
		</ol>
		<!--
			#637: one item per member and vendor whose runs wait on a tool not connected for them: who, which tool, and
			Connect to that member's Tools. It leaves once none of their runs is parked on the vendor. They are not
			approvals, so a heading and one line set them apart from the gates below; the words say "connect", as the
			Tools page does, and the raw tool names show in Advanced only.
		-->
		<section v-if="toolNotBoundCards.length" class="space-y-2" data-testid="tool-not-bound-section">
			<ZhSectionHeading>Members who can’t start work yet</ZhSectionHeading>
			<p class="text-muted-foreground text-sm">
				Their runs wait because a tool their tasks need is not connected for them. Nothing here is approved or
				rejected: connect the tool and the runs carry on.
			</p>
			<ol
				class="m-0 grid list-none gap-4 p-0 lg:grid-cols-2 2xl:grid-cols-3"
				aria-label="Tools to connect"
				data-testid="tool-not-bound-items"
			>
				<li
					v-for="item in toolNotBoundCards"
					:key="`${item.memberId}:${item.vendor}`"
					class="grid"
					:data-tool-not-bound="`${item.memberId}:${item.vendor}`"
				>
					<ZhDecisionCard
						:decision="{ title: 'A tool isn’t connected', from: item.memberName, why: toolNotBoundWhy(item) }"
						status="Runs waiting"
						status-tone="failed"
					>
						<template #actions>
							<Button as-child size="sm">
								<RouterLink :to="item.buttonPath">{{ item.buttonLabel }}</RouterLink>
							</Button>
						</template>
					</ZhDecisionCard>
				</li>
			</ol>
		</section>
		<!--
			KPIs waiting for sign-off, one card per owner. Nothing is approved here: the button opens the page where they are
			signed off, and the card leaves once none of them waits.
		-->
		<section v-if="kpiSignOffCards.length" class="space-y-2" data-testid="kpi-sign-off-section">
			<ZhSectionHeading>KPIs waiting for sign-off</ZhSectionHeading>
			<p class="text-muted-foreground text-sm">
				A KPI counts once a person signs it off. Until then no run sees it or records a reading for it.
			</p>
			<ol
				class="m-0 grid list-none gap-4 p-0 lg:grid-cols-2 2xl:grid-cols-3"
				aria-label="KPIs waiting for sign-off"
				data-testid="kpi-sign-off-items"
			>
				<li v-for="item in kpiSignOffCards" :key="item.key" class="grid" :data-kpi-sign-off="item.key">
					<ZhDecisionCard
						:decision="{ title: 'KPIs waiting for sign-off', from: item.ownerName, why: item.sentence }"
						status="Not measured yet"
						status-tone="failed"
					>
						<template #actions>
							<Button as-child size="sm">
								<RouterLink :to="item.buttonPath">{{ item.buttonLabel }}</RouterLink>
							</Button>
						</template>
					</ZhDecisionCard>
				</li>
			</ol>
		</section>
		<!--
			Issue 849: each run's proposed work items, one card per run: Approve, Hold or Drop each, or Approve all. They count in
			the sidebar's Needs you badge with the gates below.
		-->
		<section v-if="proposedBatches.length" class="space-y-2" data-testid="proposed-work-section">
			<ZhSectionHeading>Proposed work</ZhSectionHeading>
			<p class="text-muted-foreground text-sm">
				Work a run proposed for a team's list. Nothing starts until you approve it; hold keeps it on the list, drop
				takes it off.
			</p>
			<ol class="m-0 grid list-none gap-4 p-0 lg:grid-cols-2 2xl:grid-cols-3" aria-label="Proposed work">
				<li v-for="batch in proposedBatches" :key="batch.runId" class="grid" :data-proposed-run="batch.runId">
					<ProposedWorkCard :batch="batch" :now="now" @decided="refreshProposed()" />
				</li>
			</ol>
		</section>
		<FirstRunHint
			v-if="noGates && !proposedBatches.length && !kpiSignOffCards.length"
			lead="No approval is waiting on you yet. Approvals your tasks ask for appear here."
			:action="{ label: 'Go to Tasks', to: '/tasks' }"
		/>
		<template v-else-if="!noGates">
			<div class="flex flex-wrap items-end justify-between gap-2" data-testid="gates-toolbar">
				<div class="flex flex-wrap items-end gap-2">
					<label class="grid gap-1 text-sm">
						<span class="text-muted-foreground">Search</span>
						<ListSearchField v-model="table.search.value" type="search" class="min-w-48" placeholder="Search" />
					</label>
					<!--
						The words above each Select name its trigger; `aria-label` lands on the field the kit Select
						mirrors its choice on.
					-->
					<div class="grid gap-1 text-sm">
						<span id="gates-filter-execution" class="text-muted-foreground">Execution</span>
						<Select
							:model-value="filterChoice('execution')"
							aria-label="Execution"
							@update:model-value="chooseFilter('execution', $event)"
						>
							<SelectTrigger class="min-w-40" aria-labelledby="gates-filter-execution">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem :value="ALL">All</SelectItem>
								<SelectItem v-for="execution in executions" :key="execution" :value="execution">
									{{ execution }}
								</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<div class="grid gap-1 text-sm">
						<span id="gates-filter-tool" class="text-muted-foreground">Tool</span>
						<Select
							:model-value="filterChoice('tool')"
							aria-label="Tool"
							@update:model-value="chooseFilter('tool', $event)"
						>
							<SelectTrigger class="min-w-40" aria-labelledby="gates-filter-tool">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem :value="ALL">All</SelectItem>
								<SelectItem v-for="tool in tools" :key="tool" :value="tool">{{ tool }}</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<div class="grid gap-1 text-sm">
						<span id="gates-sort" class="text-muted-foreground">Sort</span>
						<Select :model-value="sortKey" aria-label="Sort" @update:model-value="chooseSort">
							<SelectTrigger class="min-w-40" aria-labelledby="gates-sort">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem v-for="option in SORTS" :key="option.key" :value="option.key">
									{{ option.label }}
								</SelectItem>
							</SelectContent>
						</Select>
					</div>
				</div>
				<Button v-if="narrowed" type="button" size="sm" variant="ghost" @click="clearFilters">
					<RotateCcw class="size-4" />
					Clear filters
				</Button>
			</div>

			<p v-if="loading" class="text-muted-foreground text-sm">Loading…</p>
			<p v-else-if="table.total.value === 0" class="text-muted-foreground text-sm">No gate matches.</p>
			<!--
				Only the cards' moves are transitions: a decided card plays its own way out before it leaves the list,
				so it leaves at once (`css` off) and the cards after it slide into its place, as the kit moves a
				`zh-decision` list. Each card fills its row, so cards side by side stand the same height and their
				buttons share a line. An aged card's failed-tone edge is the card's own, from its status tone.
			-->
			<TransitionGroup
				v-else
				tag="ol"
				name="zh-decision"
				:css="false"
				class="m-0 grid list-none gap-4 p-0 lg:grid-cols-2 2xl:grid-cols-3"
				aria-label="Gates waiting on you"
			>
				<li
					v-for="card in table.paginated.value"
					:key="card.id"
					class="grid"
					:data-gate-id="card.id"
					:data-alert="card.alert || undefined"
				>
					<ZhDecisionCard
						:decision="card.decision"
						:status="ALERT[card.alert].status"
						:status-tone="ALERT[card.alert].tone"
						:decided="card.decided"
						:decided-label="card.decidedLabel"
						:kind="card.kind"
					>
						<template #from>
							<!-- The team asking leads in the card's ink; with no team known, the execution stands in, as a link. -->
							<span v-if="card.askedBy?.team" class="text-foreground">{{ card.askedBy.team }}</span>
							<RouterLink
								v-else-if="card.executionId"
								class="text-foreground underline-offset-2 hover:underline"
								:to="`/executions/${card.executionId}`"
							>
								{{ card.execution }}
							</RouterLink>
							<span v-else>No execution</span>
							<template v-if="card.decision.by"> / {{ card.decision.by }}</template>
							<template v-if="advancedMode"> / {{ card.toolName }}</template>
						</template>
						<template #title>
							<!-- The run's own title says what approving does; without one, the task it is for, as a link. -->
							<template v-if="card.title">{{ card.title }}</template>
							<RouterLink
								v-else-if="card.taskId"
								class="text-foreground underline-offset-2 hover:underline"
								:to="`/tasks/${card.taskId}`"
							>
								{{ card.taskLabel }}
							</RouterLink>
							<template v-else>{{ card.taskLabel }}</template>
						</template>
						<!-- A card leads with one line of its summary and the recommendation it carries (#1760). -->
						<!-- A CEO question writes no summary: its questions, answered in the card, are what you decide on. -->
						<!-- What a gate with choices decides on is drawn whenever it has any, lead or no lead. -->
						<template
							v-if="card.lead || card.shown.length || (!card.summaryText && ceoQuestions(card) === null)"
							#body
						>
							<ZhDecisionLead v-if="card.lead" :line="card.lead.line" :recommendation="card.lead.recommendation" />
							<p v-else-if="!card.summaryText" class="text-warning-amber text-sm">
								No summary. <RouterLink class="underline" :to="`/runs/${card.runId}`">Open the run</RouterLink> before
								you decide.
							</p>
							<!-- A gate with its own choices keeps what it decides on in view, under its lead. -->
							<ZhDecisionSections v-if="card.shown.length" :sections="card.shown" />
						</template>
						<!-- The rest of what the run wrote, under its headings, one press away. -->
						<template v-if="card.folded.length" #details>
							<ZhDecisionSections :sections="card.folded" />
						</template>
						<template #actions>
							<!-- Questions are answered one by one, in the card as in chat, not with a bare click. -->
							<!-- Whatever the form sends goes on whole, an edit included (os#911), exactly as the run page does. -->
							<div v-if="ceoQuestions(card) !== null" class="w-full space-y-3 text-sm">
								<GateDecisionActions :gate="card" :busy="!!card.decided" @decide="decideQuestions">
									<template #link><GateCardLink :gate="card" /></template>
								</GateDecisionActions>
							</div>
							<!-- A gate with its own choices offers those, with what each asks for, not Approve and Reject. -->
							<div v-else-if="card.choices?.length" class="w-full space-y-3 text-sm">
								<GateChoiceForm
									:gate="card"
									:busy="!!card.decided"
									@decide="(id, decision, note, choice) => decideChoice(card, id, decision, note, choice)"
								>
									<template #link><GateCardLink :gate="card" /></template>
								</GateChoiceForm>
							</div>
							<!-- The row every decision card shares with zerohuman.com: Approve, Reject, then where to look. -->
							<ZhDecisionActions
								v-else
								:busy="!!card.decided"
								@approve="decide(card.id, 'approve')"
								@reject="decide(card.id, 'reject')"
							>
								<!-- Feedback needs a note, so Request changes lives on the run page. -->
								<RouterLink v-if="card.requestChanges" :to="`/runs/${card.runId}`">Request changes</RouterLink>
								<template #link><GateCardLink :gate="card" /></template>
							</ZhDecisionActions>
						</template>
					</ZhDecisionCard>
				</li>
			</TransitionGroup>

			<div class="text-muted-foreground flex flex-wrap items-center justify-between gap-2 text-sm">
				<p>{{ shown }}</p>
				<div v-if="table.pages.value > 1" class="flex items-center gap-2">
					<span>Page {{ table.page.value }} of {{ table.pages.value }}</span>
					<Button type="button" size="sm" variant="outline" :disabled="table.page.value <= 1" @click="table.page.value -= 1">
						Previous
					</Button>
					<Button
						type="button"
						size="sm"
						variant="outline"
						:disabled="table.page.value >= table.pages.value"
						@click="table.page.value += 1"
					>
						Next
					</Button>
				</div>
			</div>
		</template>
	</section>
</template>
